import { useState, useEffect, useReducer } from "react";
import ProductsTable from "../../components/products/ProductsTable";
import { Product } from "../../components/products/ProductInterface";
import { getProductsPage, putProduct } from "../../services/products/productAxios";
import ProductsTableSkeleton from "../../components/skeleton/ProductsTableSkeleton";
import { enqueueSnackbar } from "notistack";
// import { useCurrency } from "../../context/Currency";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import ProductsHeader from "../../components/products/ProductsHeader";
import {
  productsReducer,
  initialProductState,
} from "../../reducers/productPage/ProductPageReducer";
import toast from "react-hot-toast";
import { Search } from "lucide-react";

const PRODUCTS_PAGE_SIZE = 15;

const getVisiblePaginationPages = (currentPage: number, totalPages: number) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index);
  }

  const pages = new Set([0, totalPages - 1, currentPage]);
  if (currentPage > 0) pages.add(currentPage - 1);
  if (currentPage < totalPages - 1) pages.add(currentPage + 1);

  const sortedPages = Array.from(pages).sort((a, b) => a - b);
  return sortedPages.reduce<(number | "ellipsis")[]>((items, page, index) => {
    if (index > 0 && page - sortedPages[index - 1] > 1) {
      items.push("ellipsis");
    }
    items.push(page);
    return items;
  }, []);
};

export default function ProductPage() {
  const { language } = useAuth();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
  });
  const [productsState, dispatchProducts] = useReducer(
    productsReducer,
    initialProductState
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPage(0);
      setDebouncedSearch(search.trim());
    }, 450);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    dispatchProducts({ type: "INIT_FETCH_PRODUCTS" });
    getProductsPage({
      page,
      size: PRODUCTS_PAGE_SIZE,
      search: debouncedSearch || undefined,
    })
      .then((response) => {
        dispatchProducts({ type: "SET_PRODUCTS", payload: response.items });
        setPagination({
          totalItems: response.total_items,
          totalPages: response.total_pages,
        });
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
        enqueueSnackbar("Error fetching products", { variant: "error" });
        dispatchProducts({ type: "SET_ERROR", payload: error });
      });
  }, [page, debouncedSearch]);

  const saveAllChanges = async () => {
    if (productsState.editingIds.size === 0) return;
    
    const updatedProducts = productsState.products.map(product => {
      if (productsState.editingIds.has(product.id)) {
        const editValues = productsState.editValuesMap[product.id];
        return {
          ...product,
          name: editValues.name,
          price: parseFloat(editValues.price),
        };
      }
      return product;
    }).filter(product => productsState.editingIds.has(product.id));
    
    try {
      await Promise.all(updatedProducts.map(async product => {
        await putProduct(product)
    }));
      
      dispatchProducts({ type: "SAVE_CHANGES" });
      toast.success(`${updatedProducts.length} ${dictionary["products-updated-successfully"][language] || "products updated successfully"}`);
      
    } catch (error) {
      console.error("Error updating products:", error);
      toast.error(dictionary["error-updating-products"][language] || "Error updating products. Please try again.");
    }
  };

  const startItem = pagination.totalItems === 0 ? 0 : page * PRODUCTS_PAGE_SIZE + 1;
  const endItem = Math.min((page + 1) * PRODUCTS_PAGE_SIZE, pagination.totalItems);
  if (productsState.isLoading) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] px-16 pt-10 pb-16 font-['Plus_Jakarta_Sans']">
        <ProductsHeader
          title={dictionary["products"][language]}
          description=""
        />
        <div className="mt-4">
          <ProductsTableSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6FA] px-16 pt-10 pb-16 font-['Plus_Jakarta_Sans']">
      <ProductsHeader
        title={dictionary["products"][language]}
        description=""
        productsState={productsState}
        dispatchProducts={dispatchProducts}
        onSaveChanges={saveAllChanges}
      />
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar producto"
            className="h-11 w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 text-sm text-gray-700 outline-none transition-colors placeholder:text-gray-400 focus:border-fluxi"
          />
        </div>
      </div>
      <div className="mt-4">
        <ProductsTable
          productsState={productsState}
          dispatchProducts={dispatchProducts}
          footer={
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-10 py-7 border-t border-gray-100 bg-[#F7F8FC] text-sm text-gray-600">
              <span>
                Mostrando {startItem} - {endItem} de {pagination.totalItems}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((currentPage) => Math.max(currentPage - 1, 0))}
                  disabled={page <= 0 || productsState.isLoading}
                  className="h-10 px-4 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-600 transition-colors hover:border-fluxi hover:text-fluxi disabled:bg-white disabled:text-gray-300 disabled:hover:border-gray-200"
                >
                  Anterior
                </button>
                {getVisiblePaginationPages(page, pagination.totalPages).map((pageNumber, index) =>
                  pageNumber === "ellipsis" ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="h-10 min-w-8 inline-flex items-center justify-center text-sm text-gray-500"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={pageNumber}
                      type="button"
                      onClick={() => setPage(pageNumber)}
                      disabled={productsState.isLoading}
                      className={`h-10 min-w-10 rounded-md border text-sm font-medium transition-colors ${
                        pageNumber === page
                          ? "border-fluxi bg-fluxi text-white shadow-sm"
                          : "border-gray-200 bg-white text-gray-700 hover:border-fluxi hover:text-fluxi"
                      }`}
                    >
                      {pageNumber + 1}
                    </button>
                  )
                )}
                <button
                  type="button"
                  onClick={() => setPage((currentPage) => currentPage + 1)}
                  disabled={page + 1 >= pagination.totalPages || productsState.isLoading}
                  className="h-10 px-4 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:border-fluxi hover:text-fluxi disabled:bg-white disabled:text-gray-300 disabled:hover:border-gray-200"
                >
                  Siguiente
                </button>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
}
